/*
 * YAMU CRO: Size chart modal.
 * Single instance per page. Loads chart data from yamu-size-charts.json on first open.
 * The clicked link's [data-yamu-sc-product-chart] (set by the snippet) tells us which chart key to show.
 */
(function () {
  'use strict';

  // Init guard: the snippet's <script> can execute more than once if the buy box
  // is rendered for both desktop and mobile/sticky layouts. Without this, each
  // load binds another document click listener and the modal opens twice.
  if (window.__yamuSizeChartInit) return;
  window.__yamuSizeChartInit = true;

  var CHARTS_URL = window.YamuSizeChartsUrl || '/files/yamu-size-charts.json';
  var DIAGRAMS_URL = window.YamuSizeDiagramsUrl || {}; // map: 'tshirt'|'jean'|'short' -> svg url

  // The snippet rewrites these through Shopify asset_url at runtime via globals set in Liquid.

  var chartsPromise = null;
  function loadCharts() {
    if (chartsPromise) return chartsPromise;
    // Prefer charts injected inline from Shopify metaobjects (staff-managed in
    // Admin, theme-independent). Fall back to the bundled JSON file if absent.
    if (window.YamuSizeChartsData &&
        typeof window.YamuSizeChartsData === 'object' &&
        Object.keys(window.YamuSizeChartsData).length) {
      chartsPromise = Promise.resolve(window.YamuSizeChartsData);
      return chartsPromise;
    }
    chartsPromise = fetch(CHARTS_URL, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('size chart load: ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        console.error('[yamu-size-chart] failed to load charts:', err);
        return {};
      });
    return chartsPromise;
  }

  function isEmptyVal(v) {
    return v == null || v === '' || v === '-';
  }

  function inToCm(v) {
    if (typeof v !== 'number') return v;
    return Math.round(v * 2.54 * 10) / 10;
  }

  function fmt(v, unit) {
    if (isEmptyVal(v)) return '';
    // Values may arrive as numbers (JSON file) or strings (metaobjects).
    var n = typeof v === 'number' ? v : parseFloat(v);
    var isNumeric = !isNaN(n) && String(n) === String(v).trim();
    if (isNumeric) {
      return unit === 'cm' ? String(inToCm(n)) : String(n);
    }
    // Non-numeric (ranges, fractions like "5 1/2", "3 1/2 X 4 1/2") -> show as-is
    return String(v);
  }

  function buildTable(modal, chart, blockIndex, unit) {
    var wrap = modal.querySelector('[data-yamu-sc-table]');
    var block = chart.blocks[blockIndex] || chart.blocks[0];
    if (!block) {
      wrap.innerHTML = '<p style="padding:24px;text-align:center;color:#888">No data available.</p>';
      return;
    }

    var html = '<table class="yamu-sc-table"><thead><tr><th>Measurement</th>';
    block.sizes.forEach(function (s) {
      html += '<th>' + s + '</th>';
    });
    html += '</tr></thead><tbody>';

    block.measurements.forEach(function (row) {
      html += '<tr><td>' + row.label + '</td>';
      row.values.forEach(function (v) {
        if (isEmptyVal(v)) {
          html += '<td class="yamu-sc-empty">&mdash;</td>';
        } else {
          html += '<td>' + fmt(v, unit) + '</td>';
        }
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
    wrap.innerHTML = html;
  }

  function buildTabs(modal, chart) {
    var tabs = modal.querySelector('[data-yamu-sc-tabs]');
    tabs.innerHTML = '';
    if (chart.blocks.length <= 1) return;
    chart.blocks.forEach(function (block, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'yamu-sc-tabs__btn' + (i === 0 ? ' is-active' : '');
      btn.dataset.yamuScTab = String(i);
      btn.textContent = labelForBlock(block);
      tabs.appendChild(btn);
    });
  }

  function labelForBlock(block) {
    var first = block.sizes[0];
    var last = block.sizes[block.sizes.length - 1];
    // Heuristic: letter sizes
    if (/^[SMLXB]/i.test(first)) return first + ' to ' + last;
    return first + ' to ' + last;
  }

  // Merge all size blocks (e.g. S-3XL + 3XB-6XB) into one table: sizes become
  // one continuous column set, measurement rows are matched by label and their
  // values concatenated. Single-block charts pass through unchanged.
  function mergedBlock(chart) {
    var blocks = (chart && chart.blocks) || [];
    if (blocks.length === 0) return null;
    if (blocks.length === 1) return blocks[0];
    var sizes = [];
    blocks.forEach(function (b) { sizes = sizes.concat(b.sizes); });
    var labels = [], byLabel = {};
    blocks.forEach(function (b) {
      b.measurements.forEach(function (m) {
        if (!(m.label in byLabel)) { byLabel[m.label] = []; labels.push(m.label); }
      });
    });
    labels.forEach(function (lbl) {
      blocks.forEach(function (b) {
        var row = null;
        b.measurements.forEach(function (m) { if (m.label === lbl) row = m; });
        var vals = row ? row.values : b.sizes.map(function () { return null; });
        byLabel[lbl] = byLabel[lbl].concat(vals);
      });
    });
    return { sizes: sizes, measurements: labels.map(function (l) { return { label: l, values: byLabel[l] }; }) };
  }

  // Render a single chart (diagram + one merged, scrollable table) into a slot.
  function renderChartSlot(slot, chart, unit) {
    var tableWrap = slot.querySelector('[data-yamu-sc-table]');
    var diagEl = slot.querySelector('[data-yamu-sc-diagram]');
    var titleEl = slot.querySelector('[data-yamu-sc-slot-title]');

    if (titleEl) titleEl.textContent = chart.title || '';

    if (diagEl) {
      diagEl.innerHTML = '';
      var dkey = chart.diagram;
      var dUrl = dkey && DIAGRAMS_URL[dkey];
      if (dUrl) {
        fetch(dUrl, { credentials: 'omit' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (svg) { if (svg) diagEl.innerHTML = svg; })
          .catch(function () {});
      }
    }

    var block = mergedBlock(chart);
    if (!block) {
      tableWrap.innerHTML = '<p style="padding:24px;text-align:center;color:#888">No data.</p>';
      return;
    }
    // All sizes in one horizontally-scrollable table (no tabs).
    tableWrap.innerHTML = renderTableHtml(block, unit);
  }

  function renderTableHtml(block, unit) {
    var html = '<table class="yamu-sc-table"><thead><tr><th>Measurement</th>';
    block.sizes.forEach(function (s) { html += '<th>' + s + '</th>'; });
    html += '</tr></thead><tbody>';
    block.measurements.forEach(function (row) {
      html += '<tr><td>' + row.label + '</td>';
      row.values.forEach(function (v) {
        if (isEmptyVal(v)) {
          html += '<td class="yamu-sc-empty">&mdash;</td>';
        } else {
          html += '<td>' + fmt(v, unit) + '</td>';
        }
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
    return html;
  }

  function openModal(modal, chartKey) {
    // Enforce a single modal instance. If the buy box rendered more than once
    // there can be duplicate modal nodes; drop all but the one we are opening.
    document.querySelectorAll('[data-yamu-sc-modal]').forEach(function (m) {
      if (m !== modal && m.parentNode) m.parentNode.removeChild(m);
    });
    // Detach the modal from its parent and append it to <body> so that
    // position: fixed is not constrained by any transformed/clipped ancestor
    // (motion-element, scroll containers, sticky headers, etc).
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    loadCharts().then(function (charts) {
      // Support combo keys "top|bottom" for sets
      var keys = String(chartKey).split('|').map(function (k) { return k.trim(); }).filter(Boolean);
      var primary = charts[keys[0]];
      if (!primary) {
        console.warn('[yamu-size-chart] unknown chart key:', chartKey);
        return;
      }
      modal.dataset.yamuScChart = chartKey;
      modal.dataset.yamuScUnit = 'in';

      var headTitle = modal.querySelector('[data-yamu-sc-title]');
      var headDesc = modal.querySelector('[data-yamu-sc-desc]');
      var body = modal.querySelector('[data-yamu-sc-slots]');
      if (!body) {
        // legacy single-slot fallback (no .yamu-sc-slots wrapper)
        body = modal;
      }

      if (keys.length === 1) {
        headTitle.textContent = primary.title || 'Size chart';
        headDesc.textContent = primary.description || '';
        body.innerHTML = '<div class="yamu-sc-slot" data-yamu-sc-slot>' +
          '<div class="yamu-sc-diagram" data-yamu-sc-diagram></div>' +
          '<div class="yamu-sc-table-wrap" data-yamu-sc-table></div>' +
        '</div>';
        var slot = body.querySelector('[data-yamu-sc-slot]');
        renderChartSlot(slot, primary, 'in');
      } else {
        // Combo: render multiple stacked slots
        headTitle.textContent = 'Size guide: Set';
        headDesc.textContent = 'This set includes a top and a bottom. Measurements for each are shown below.';
        var slotsHtml = '';
        keys.forEach(function (k, i) {
          slotsHtml += '<div class="yamu-sc-slot" data-yamu-sc-slot data-yamu-sc-slot-key="' + k + '">' +
            '<h3 class="yamu-sc-slot-heading" data-yamu-sc-slot-title></h3>' +
            '<div class="yamu-sc-diagram" data-yamu-sc-diagram></div>' +
            '<div class="yamu-sc-table-wrap" data-yamu-sc-table></div>' +
          '</div>';
          if (i < keys.length - 1) {
            slotsHtml += '<div class="yamu-sc-divider" role="presentation"></div>';
          }
        });
        body.innerHTML = slotsHtml;
        body.querySelectorAll('[data-yamu-sc-slot]').forEach(function (slot) {
          var k = slot.dataset.yamuScSlotKey;
          var c = charts[k];
          if (c) renderChartSlot(slot, c, 'in');
        });
      }

      modal.querySelectorAll('[data-yamu-sc-unit]').forEach(function (b) {
        b.classList.toggle('is-active', b.dataset.yamuScUnit === 'in');
      });

      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('yamu-sc-open');

      setTimeout(function () {
        var closeBtn = modal.querySelector('[data-yamu-sc-close]');
        if (closeBtn) closeBtn.focus();
      }, 100);
    });
  }

  function closeModal(modal) {
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('yamu-sc-open');
  }

  function wireUp() {
    // Use event delegation since the link may be inside a section that re-renders on variant change
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-yamu-sc-open]');
      if (trigger) {
        e.preventDefault();
        var wrap = trigger.closest('[data-yamu-sc-product-chart]');
        var key = wrap ? wrap.dataset.yamuScProductChart : null;
        if (!key) return;
        // Find the modal in same section, or the first one on the page
        var section = trigger.closest('.shopify-section') || document;
        var modal = section.querySelector('[data-yamu-sc-modal]') || document.querySelector('[data-yamu-sc-modal]');
        if (modal) openModal(modal, key);
        return;
      }
      var closer = e.target.closest('[data-yamu-sc-close]');
      if (closer) {
        var modal = closer.closest('[data-yamu-sc-modal]');
        if (modal) closeModal(modal);
        return;
      }
      var tab = e.target.closest('[data-yamu-sc-tab]');
      if (tab) {
        var modal = tab.closest('[data-yamu-sc-modal]');
        if (!modal) return;
        var key = modal.dataset.yamuScChart;
        var unit = modal.dataset.yamuScUnit || 'in';
        var blockIdx = parseInt(tab.dataset.yamuScTab, 10) || 0;
        modal.dataset.yamuScBlock = String(blockIdx);
        modal.querySelectorAll('[data-yamu-sc-tab]').forEach(function (b) {
          b.classList.toggle('is-active', b === tab);
        });
        loadCharts().then(function (charts) {
          buildTable(modal, charts[key], blockIdx, unit);
        });
        return;
      }
      var unitBtn = e.target.closest('[data-yamu-sc-unit]');
      if (unitBtn) {
        var modal = unitBtn.closest('[data-yamu-sc-modal]');
        if (!modal) return;
        var key = modal.dataset.yamuScChart;
        var unit = unitBtn.dataset.yamuScUnit;
        modal.dataset.yamuScUnit = unit;
        modal.querySelectorAll('[data-yamu-sc-unit]').forEach(function (b) {
          b.classList.toggle('is-active', b === unitBtn);
        });
        loadCharts().then(function (charts) {
          // Re-render every slot's merged table with the new unit
          modal.querySelectorAll('[data-yamu-sc-slot]').forEach(function (slot) {
            var k = slot.dataset.yamuScSlotKey || key.split('|')[0];
            var chart = charts[k];
            if (!chart) return;
            slot.querySelector('[data-yamu-sc-table]').innerHTML = renderTableHtml(mergedBlock(chart), unit);
          });
        });
        return;
      }
    });

    // Escape key closes the modal
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('[data-yamu-sc-modal][aria-hidden="false"]');
      if (open) closeModal(open);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireUp);
  } else {
    wireUp();
  }
})();
